import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Wallet, Dice5, Menu, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface Tab {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Section match so the tab stays lit across all of its child routes. */
  match: (path: string) => boolean;
}

const TABS: Tab[] = [
  { label: 'Home', to: '/', icon: LayoutDashboard, match: (p) => p === '/' },
  { label: 'Members', to: '/users', icon: Users, match: (p) => p.startsWith('/users') },
  { label: 'Finance', to: '/finance/recharges', icon: Wallet, match: (p) => p.startsWith('/finance') },
  { label: 'Games', to: '/games', icon: Dice5, match: (p) => p.startsWith('/games') },
];

/**
 * Phone-only bottom navigation. Primary destinations as large touch targets plus
 * a "Menu" button that opens the full drawer. Hidden from lg+ where the sidebar lives.
 */
export function MobileNav({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { pathname } = useLocation();

  return (
    <nav
      aria-label="Primary"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 lg:hidden',
        'border-t border-border/80 bg-background/85 backdrop-blur-lg',
        'shadow-[0_-8px_24px_-16px_rgba(0,0,0,0.35)]',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <ul className="mx-auto grid max-w-md grid-cols-5">
        {TABS.map((tab) => {
          const active = tab.match(pathname);
          const Icon = tab.icon;
          return (
            <li key={tab.to}>
              <Link
                to={tab.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'group relative flex h-16 flex-col items-center justify-center gap-1',
                  'transition-colors duration-200 active:scale-[0.92] motion-safe:transition-transform',
                  active ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {/* Active accent bar */}
                <span
                  className={cn(
                    'absolute inset-x-4 top-0 h-0.5 rounded-full bg-primary transition-opacity duration-300',
                    active ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
                    active ? 'bg-primary/12' : 'group-active:bg-accent',
                  )}
                >
                  <Icon className={cn('h-[22px] w-[22px]', active && 'scale-105')} />
                </span>
                <span className="text-[10.5px] font-medium leading-none tracking-tight">{tab.label}</span>
              </Link>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label="Open menu"
            className={cn(
              'group flex h-16 w-full flex-col items-center justify-center gap-1',
              'text-muted-foreground transition-colors duration-200 active:scale-[0.92] motion-safe:transition-transform',
            )}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200 group-active:bg-accent">
              <Menu className="h-[22px] w-[22px]" />
            </span>
            <span className="text-[10.5px] font-medium leading-none tracking-tight">Menu</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
